import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell, CarFront, CheckCircle2, Circle, Clock, CreditCard, DollarSign, FileBadge,
  Loader2, MapPin, MessageCircle, Shield, ShoppingBag, Signature, ThumbsUp, User, X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ChefHeader from '../shared/ui/ChefHeader';
import { PrimaryButton } from '../shared/ui';
import { listenToDriver, setDriverOnline, updateUserProfile } from '../shared/services/userService';
import PermitSheet from '../components/profile/PermitSheet';
import DocumentSheet from '../components/profile/DocumentSheet';
import LegalSheet from '../components/profile/LegalSheet';
import { requiredDriverDocs, docStatusLabel } from '../shared/documents';

const DOC_ICONS = { selfie: User, driversLicense: CreditCard, vehiclePhoto: CarFront, deliveryBag: ShoppingBag };
import { requestNotificationPermissionAndSaveToken } from '../shared/firebase';

const WELCOME_KEY = 'hasSeenDriverWelcome'; // mirrors iOS UserDefaults key

// iOS ChecklistItem: icon, title, description, green check circle when done.
function ChecklistItem({ icon: Icon, title, description, done, onTap, busy = false }) {
  return (
    <button
      type="button"
      onClick={onTap}
      disabled={busy}
      className="w-full flex items-center gap-3 bg-white rounded-2xl p-4 shadow-sm border border-gray-100 text-left active:bg-gray-50"
    >
      <span className="w-10 h-10 rounded-full bg-orange-50 text-[#FF6B35] flex items-center justify-center shrink-0">
        {busy ? <Loader2 size={18} className="animate-spin" /> : <Icon size={19} />}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-gray-900">{title}</span>
        <span className="block text-xs text-gray-400 mt-0.5">{description}</span>
      </span>
      {done ? (
        <CheckCircle2 size={22} className="text-green-500 shrink-0" />
      ) : (
        <Circle size={22} className="text-gray-300 shrink-0" />
      )}
    </button>
  );
}

// iOS HowItWorksStep: numbered circle + title + description card.
function HowItWorksStep({ number, title, description }) {
  return (
    <div className="flex items-start gap-4 bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <span className="w-10 h-10 rounded-full bg-orange-100 text-[#FF6B35] flex items-center justify-center font-bold shrink-0">
        {number}
      </span>
      <span className="flex-1">
        <span className="block text-sm font-bold text-gray-900">{title}</span>
        <span className="block text-xs text-gray-400 mt-0.5">{description}</span>
      </span>
    </div>
  );
}

// iOS TipCard.
function TipCard({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-3 bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
      <span className="w-9 h-9 rounded-full bg-orange-50 text-[#FF6B35] flex items-center justify-center shrink-0">
        <Icon size={17} />
      </span>
      <span className="flex-1">
        <span className="block text-sm font-bold text-gray-900">{title}</span>
        <span className="block text-xs text-gray-400 mt-0.5">{description}</span>
      </span>
    </div>
  );
}

export default function WelcomePage() {
  const { user, userProfile, driver, driverId, refreshDriver } = useAuth();
  const navigate = useNavigate();

  // Live driver doc so vehicle edits / online status update the checklist.
  const [liveDriver, setLiveDriver] = useState(driver);
  useEffect(() => {
    if (!driverId) return undefined;
    return listenToDriver(driverId, d => setLiveDriver(d));
  }, [driverId]);
  const drv = liveDriver || driver;

  const [geoGranted, setGeoGranted] = useState(false);
  const [geoHint, setGeoHint] = useState('');
  const [notifGranted, setNotifGranted] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted'
  );
  const [notifHint, setNotifHint] = useState('');
  const [busyKey, setBusyKey] = useState(null);

  // Watch geolocation permission state (Permissions API).
  useEffect(() => {
    let status;
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then(s => {
          status = s;
          setGeoGranted(s.state === 'granted');
          s.onchange = () => setGeoGranted(s.state === 'granted');
        })
        .catch(() => {});
    }
    return () => { if (status) status.onchange = null; };
  }, []);

  // ---- Checklist completion (mirrors DriverWelcomeViewModel) ----
  const profileDone = useMemo(() => {
    const p = userProfile || {};
    const hasName =
      `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.name || drv?.name || '';
    const phone = p.phone || p.phoneNumber || drv?.phone || '';
    return !!(String(hasName).trim() && String(phone).trim());
  }, [userProfile, drv]);

  const vehicleDone = !!(drv?.vehicleType && drv?.vehicleNumber);
  const isOnline = !!drv?.isOnline;
  const permitDone = !!(drv?.foodHandlerPermitURL || drv?.raw?.foodHandlerPermitURL);
  const [legalAccepted, setLegalAccepted] = useState(!!userProfile?.acceptedLegalTerms);
  useEffect(() => { setLegalAccepted(!!userProfile?.acceptedLegalTerms); }, [userProfile]);
  const [permitOpen, setPermitOpen] = useState(false);
  const [activeDoc, setActiveDoc] = useState(null);
  const [legalDoc, setLegalDoc] = useState(null); // 'terms' | 'liability' | null

  // Verification docs required for this driver's vehicle type — mirrors
  // iOS DriverDocumentType.required.
  const requiredDocs = requiredDriverDocs(drv?.vehicleType);

  const items = [
    { key: 'profile', done: profileDone },
    { key: 'vehicle', done: vehicleDone },
    { key: 'permit', done: permitDone },
    ...requiredDocs.map(d => ({ key: `doc:${d.key}`, done: docStatusLabel(drv, d) !== 'Required' })),
    { key: 'legal', done: legalAccepted },
    { key: 'location', done: geoGranted },
    { key: 'notifications', done: notifGranted },
    { key: 'online', done: isOnline },
  ];
  const completedCount = items.filter(i => i.done).length;
  const progressPct = Math.round((completedCount / items.length) * 100);

  async function requestLocation() {
    if (!navigator.geolocation) {
      setGeoHint('Location services are not supported by this browser.');
      return;
    }
    setBusyKey('location');
    setGeoHint('');
    navigator.geolocation.getCurrentPosition(
      () => { setGeoGranted(true); setBusyKey(null); },
      () => {
        setGeoHint('Location permission was denied. Enable it in your browser settings, then tap to retry.');
        setBusyKey(null);
      },
      { timeout: 10000 }
    );
  }

  async function requestNotifications() {
    setBusyKey('notifications');
    setNotifHint('');
    try {
      await requestNotificationPermissionAndSaveToken(user);
      const granted = typeof Notification !== 'undefined' && Notification.permission === 'granted';
      setNotifGranted(granted);
      if (!granted) {
        setNotifHint('Notifications are blocked. Enable them in your browser settings, then tap to retry.');
      }
    } catch (e) {
      console.error('notification permission', e);
      setNotifHint('Could not enable notifications. Please try again.');
    } finally {
      setBusyKey(null);
    }
  }

  async function goOnline() {
    if (isOnline || !driverId || !user) return;
    setBusyKey('online');
    try {
      await setDriverOnline(driverId, user.uid, true);
      refreshDriver?.();
    } catch (e) {
      console.error('go online', e);
    } finally {
      setBusyKey(null);
    }
  }

  function finish() {
    try { localStorage.setItem(WELCOME_KEY, 'true'); } catch {}
    navigate('/driver/work');
  }

  return (
    <div>
      <ChefHeader
        title="Welcome"
        right={
          <button
            type="button"
            onClick={finish}
            className="w-8 h-8 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        }
      />

      <div className="px-4 py-5 space-y-6">
        {/* Header */}
        <div className="flex flex-col items-center text-center gap-2">
          <span className="w-20 h-20 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FF3B30] text-white flex items-center justify-center shadow-sm">
            <CarFront size={40} />
          </span>
          <h1 className="text-2xl font-extrabold text-gray-900 mt-1">Welcome Driver!</h1>
          <p className="text-sm text-gray-500">
            Let's get you ready to start delivering and earning
          </p>
        </div>

        {/* Getting Started Checklist */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between px-1">
            <p className="text-base font-bold text-gray-900">Getting Started Checklist</p>
            <p className="text-xs font-semibold text-gray-400">{completedCount} of {items.length}</p>
          </div>
          <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full rounded-full bg-[#FF6B35] transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          <ChecklistItem
            icon={User}
            title="Complete Your Driver Profile"
            description="Add your name, phone number, and profile photo"
            done={profileDone}
            onTap={() => navigate('/driver/profile')}
          />
          <ChecklistItem
            icon={CarFront}
            title="Add Vehicle Information"
            description="Enter your vehicle type and license plate"
            done={vehicleDone}
            onTap={() => navigate('/driver/profile')}
          />
          <ChecklistItem
            icon={FileBadge}
            title="Upload Food Handler's Permit"
            description="Submit a photo of your valid food handler's permit for review"
            done={permitDone}
            onTap={() => setPermitOpen(true)}
          />
          {requiredDocs.map(doc => (
            <ChecklistItem
              key={doc.key}
              icon={DOC_ICONS[doc.key] || FileBadge}
              title={doc.key === 'selfie' ? 'Take a Selfie' : `Submit ${doc.title}`}
              description={doc.instructions}
              done={docStatusLabel(drv, doc) !== 'Required'}
              onTap={() => setActiveDoc(doc)}
            />
          ))}
          <ChecklistItem
            icon={Signature}
            title="Review & Accept Legal Terms"
            description="Read and agree to the Terms of Service and Liability Waiver"
            done={legalAccepted}
            onTap={() => setLegalDoc('liability')}
          />
          <ChecklistItem
            icon={MapPin}
            title="Enable Location Services"
            description={geoHint || 'Allow location access for navigation and order tracking'}
            done={geoGranted}
            onTap={requestLocation}
            busy={busyKey === 'location'}
          />
          <ChecklistItem
            icon={Bell}
            title="Enable Notifications"
            description={notifHint || 'Get instant alerts for new delivery requests'}
            done={notifGranted}
            onTap={requestNotifications}
            busy={busyKey === 'notifications'}
          />
          <ChecklistItem
            icon={CheckCircle2}
            title="Go Online"
            description="Toggle your status to online to start accepting orders"
            done={isOnline}
            onTap={goOnline}
            busy={busyKey === 'online'}
          />
        </div>

        {/* How It Works */}
        <div className="space-y-2.5">
          <p className="px-1 text-base font-bold text-gray-900">How It Works</p>
          <HowItWorksStep
            number={1}
            title="Accept Orders"
            description="Review incoming delivery requests and accept the ones you want"
          />
          <HowItWorksStep
            number={2}
            title="Pick Up Food"
            description="Navigate to the restaurant and collect the order"
          />
          <HowItWorksStep
            number={3}
            title="Deliver to Customer"
            description="Follow GPS directions to deliver the food to the customer"
          />
          <HowItWorksStep
            number={4}
            title="Get Paid"
            description="Earn money for each delivery - track your earnings in real-time"
          />
        </div>

        {/* Pro Tips */}
        <div className="space-y-2.5">
          <p className="px-1 text-base font-bold text-gray-900">Pro Tips</p>
          <TipCard
            icon={Clock}
            title="Be Punctual"
            description="Pick up and deliver orders on time to maintain high ratings"
          />
          <TipCard
            icon={MessageCircle}
            title="Communicate"
            description="Keep customers updated if there are any delays"
          />
          <TipCard
            icon={ThumbsUp}
            title="Professional Service"
            description="Be friendly and professional to earn better tips and ratings"
          />
          <TipCard
            icon={Shield}
            title="Drive Safely"
            description="Always follow traffic rules and drive carefully"
          />
        </div>

        {/* Earnings Info */}
        <div className="flex items-center gap-3 bg-green-50 rounded-2xl p-4">
          <DollarSign size={30} className="text-green-600 shrink-0" />
          <div>
            <p className="text-sm font-bold text-gray-900">Earn Money</p>
            <p className="text-xs text-gray-500 mt-0.5">
              You'll earn a percentage of each delivery fee plus customer tips
            </p>
          </div>
        </div>

        {/* Legal Agreement — required before continuing */}
        <div className="space-y-2.5">
          <p className="px-1 text-base font-bold text-gray-900">Legal Agreement</p>
          <div className="bg-orange-50 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setLegalDoc('terms')}
                className="text-sm font-semibold text-[#FF6B35]"
              >
                📄 Terms of Service
              </button>
              <button
                type="button"
                onClick={() => setLegalDoc('liability')}
                className="text-sm font-semibold text-[#FF6B35]"
              >
                ⚠️ Liability Waiver
              </button>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (legalAccepted || !user) return;
                setLegalAccepted(true);
                try {
                  await updateUserProfile(user.uid, { acceptedLegalTerms: true });
                } catch (e) { console.error('accept terms', e); }
              }}
              className="w-full flex items-start gap-2.5 text-left"
            >
              <span className={`mt-0.5 shrink-0 ${legalAccepted ? 'text-green-500' : 'text-gray-300'}`}>
                {legalAccepted ? <CheckCircle2 size={20} /> : <Circle size={20} />}
              </span>
              <span className="text-xs text-gray-700">
                I have read and agree to the Terms of Service and Liability Waiver
              </span>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-1 pb-4">
          <PrimaryButton onClick={finish} disabled={!legalAccepted}>
            Get Started
          </PrimaryButton>
          <button
            type="button"
            onClick={finish}
            className="w-full py-2.5 text-sm font-semibold text-gray-400"
          >
            I'll Do This Later
          </button>
        </div>
      </div>

      <PermitSheet
        open={permitOpen}
        onClose={() => setPermitOpen(false)}
        driverId={driverId}
        driver={drv}
      />
      <DocumentSheet
        open={!!activeDoc}
        onClose={() => setActiveDoc(null)}
        driverId={driverId}
        driver={drv}
        doc={activeDoc}
      />
      <LegalSheet open={legalDoc === 'terms'} onClose={() => setLegalDoc(null)} doc="terms" />
      <LegalSheet open={legalDoc === 'liability'} onClose={() => setLegalDoc(null)} doc="liability" />
    </div>
  );
}
